import { Option$None, Option$Some } from 'gleam:gleam/option'
import * as uri from 'gleam:gleam/uri'
import * as _ from 'gleam:prelude'

// CONSTANTS -------------------------------------------------------------------

const defaults = {
  handle_external_links: false,
  handle_internal_links: true,
}

const initial_location = globalThis?.window?.location?.href

// EXPORTS ---------------------------------------------------------------------

export const do_initial_uri = () => {
  if (!initial_location) {
    return _.Result$Error(undefined)
  } else {
    return _.Result$Ok(uri_from_url(new URL(initial_location)))
  }
}

export const do_init = (dispatch, options = defaults) => {
  document.addEventListener('click', event => {
    const a = find_anchor(event.target)

    if (!a) return

    try {
      const url = new URL(a.href)
      const uri_ = uri_from_url(url)
      const is_external =
        url.host !== window.location.host || a.target === '_blank'

      if (!options.handle_external_links && is_external) return
      if (!options.handle_internal_links && !is_external) return

      event.preventDefault()

      if (!is_external) {
        window.history.pushState({}, '', a.href)
        window.requestAnimationFrame(() => {
          // The browser automatically attempts to scroll to an element with a matching
          // id if a hash is present in the URL. Because we need to `preventDefault`
          // the event to prevent navigation, we also need to manually scroll to the
          // element if a
          if (url.hash) {
            document.getElementById(url.hash.slice(1))?.scrollIntoView()
          } else {
            // If no hash is present, scroll to the top of the page
            window.scrollTo(0, 0)
          }
        })
      }

      return dispatch(uri_)
    } catch {
      return
    }
  })

  window.addEventListener('popstate', e => {
    e.preventDefault()

    const url = new URL(window.location.href)
    const uri_ = uri_from_url(url)

    window.requestAnimationFrame(() => {
      if (url.hash) {
        document.getElementById(url.hash.slice(1))?.scrollIntoView()
      } else {
        // If no hash is present, scroll to the top of the page
        window.scrollTo(0, 0)
      }
    })

    dispatch(uri_)
  })

  window.addEventListener('modem-push', ({ detail }) => {
    dispatch(detail)
  })

  window.addEventListener('modem-replace', ({ detail }) => {
    dispatch(detail)
  })
}

export const do_push = uri_ => {
  window.history.pushState({}, '', uri.to_string(uri_))
  window.requestAnimationFrame(() => {
    if (uri_.fragment[0]) {
      document.getElementById(uri_.fragment[0])?.scrollIntoView()
    }
  })

  window.dispatchEvent(new CustomEvent('modem-push', { detail: uri_ }))
}

export const do_replace = uri_ => {
  window.history.replaceState({}, '', uri.to_string(uri_))
  window.requestAnimationFrame(() => {
    if (uri_.fragment[0]) {
      document.getElementById(uri_.fragment[0])?.scrollIntoView()
    }
  })

  window.dispatchEvent(new CustomEvent('modem-replace', { detail: uri_ }))
}

export const do_load = uri_ => {
  window.location = uri.to_string(uri_)
}

export const do_forward = steps => {
  if (steps < 1) return

  for (let i = 0; i < steps; i++) {
    try {
      window.history.forward()
    } catch {
      continue
    }
  }
}

export const do_back = steps => {
  if (steps < 1) return

  for (let i = 0; i < steps; i++) {
    try {
      window.history.back()
    } catch {
      continue
    }
  }
}

// UTILS -----------------------------------------------------------------------

const find_anchor = el => {
  if (!el || el.tagName === 'BODY') {
    return null
  } else if (el.tagName === 'A') {
    return el
  } else {
    return find_anchor(el.parentElement)
  }
}

const uri_from_url = url => {
  const scheme = url.protocol
    ? Option$Some(url.protocol.slice(0, -1))
    : Option$None()
  const userinfo = Option$None()
  const host = url.hostname ? Option$Some(url.hostname) : Option$None()
  const port = url.port ? Option$Some(Number(url.port)) : Option$None()
  const query = url.search ? Option$Some(url.search.slice(1)) : Option$None()
  const fragment = url.hash ? Option$Some(url.hash.slice(1)) : Option$None()
  return uri.Uri$Uri(
    scheme,
    userinfo,
    host,
    port,
    url.pathname,
    query,
    fragment
  )
}
